/**
 * Deep-link fragment scroll — honour the URL's `#hash` on first load.
 *
 * Native "scroll to #hash on load" is unreliable in this kit: `<html>` carries
 * `scroll-behavior: smooth` (base/_reset.css) and Swup + async-Alpine islands
 * reflow the viewport just after first paint, so the browser's on-load fragment
 * jump lands at the top. (The separate scroll FREEZE — `overflow-x: clip` on the
 * root <html> — is prevented in _reset.css: horizontal-overflow containment lives
 * on <body>. This module handles the TIMING so the jump actually fires.) Harvested
 * from montgomery-homes (MOHO-40).
 *
 * Swup's `page:view` hook fires once the page is fully set up — after Swup's own
 * scroll handling, on every client-side visit — which is the reliable moment to
 * apply the jump for navigations that carry a hash. On the INITIAL server-rendered
 * load Swup's `page:view` never fires (the kit mints its own PAGE_VIEW_EVENT
 * instead), so a short retry backs the jump up across the boot-settle window and
 * bails the instant the user scrolls, so it never fights them.
 *
 * Pure same-page anchor CLICKS are left to the browser (the router's `ignoreVisit`
 * keeps `href="#..."` links out of Swup): the browser scrolls them live with CSS
 * smooth scrolling, honouring each target's `scroll-margin-top`.
 *
 * `behavior: 'instant'`, NOT `'auto'` — and the difference is the whole bug. This
 * said `'auto'` and described it as "an instant jump which overrides the smooth CSS".
 * It is not: per the CSSOM spec `'auto'` means *defer to the computed
 * `scroll-behavior`*, and base/_reset.css sets `scroll-behavior: smooth` on the root.
 * So every deep-link jump started a ~500ms ANIMATED scroll, and anything that touches
 * the scroll position while it is in flight cancels it outright.
 *
 * Which is exactly what happened on a cross-page Book click. Landing on /contact/#book
 * from a scrolled page, the browser first carries the old scroll offset over and clamps
 * it to the shorter document; our animated scroll then starts and is immediately
 * cancelled, so the reader is left wherever the clamp put them — for a visitor at the
 * foot of the home page, 1272px BELOW the booking widget, looking at the enquiry form.
 * It looked intermittent because a short hop finishes before anything cancels it.
 * `'instant'` lands in one frame and has nothing to interrupt.
 *
 * @param {import('swup').default} [swup] the running router instance
 * @returns {void}
 */
export function initHashScroll(swup) {
    const scrollToHash = () => {
        const hash = window.location.hash;
        if (!hash || hash === '#swup') {
            return;
        }

        let id;
        try {
            id = decodeURIComponent(hash.slice(1));
        } catch {
            id = hash.slice(1);
        }

        const target = document.getElementById(id);
        if (target) {
            target.scrollIntoView({ behavior: 'instant' });
        }
    };

    /**
     * Apply the jump, then keep re-asserting it briefly.
     *
     * ONE PASS IS NOT ENOUGH ON A SWUP VISIT, and this is the whole reason the
     * function exists rather than a bare call. Traced on a Book click from the foot of
     * the home page, in order:
     *
     *     scrollIntoView(#book)   2126 -> 750   (correct: #book 104px from the top)
     *     scrollTo(0, 0)                        \
     *     scrollTo(0, 0)                         }  @swup/scroll-plugin, after us
     *     scrollTo(0, 750)                      /
     *     ...                     -> 1408       (#book 555px ABOVE the viewport)
     *
     * We win the first write and lose the last. The plugin owns scroll for the visit
     * and runs its own reset/restore after `page:view`, so a single jump at page:view
     * is always overwritten — leaving the reader at the retained offset, clamped to
     * the shorter page, which on /contact/ is the enquiry form rather than the booking
     * widget the Book button promised.
     *
     * Re-asserting settles it without reaching into the plugin's configuration, and it
     * is the same shape the initial-load path already needed for its own reasons.
     *
     * @param {number} attempts how many times to re-assert after the first pass
     */
    const assertHashScroll = (attempts) => {
        if (!window.location.hash || window.location.hash === '#swup') {
            return;
        }

        // Bail the instant the user takes over, so a re-assert never yanks them
        // mid-scroll. Only GENUINE input counts — programmatic scrollIntoView fires
        // none of these. Re-armed per call: `once` listeners from a previous
        // navigation are long spent.
        let userTookOver = false;
        const yieldToUser = () => {
            userTookOver = true;
        };
        const opts = { passive: true, once: true };
        const events = ['wheel', 'touchmove', 'keydown'];
        events.forEach((evt) => window.addEventListener(evt, yieldToUser, opts));

        let n = 0;
        const retry = () => {
            if (userTookOver) {
                events.forEach((evt) => window.removeEventListener(evt, yieldToUser, opts));

                return;
            }
            scrollToHash();
            if (n++ < attempts) {
                setTimeout(retry, 80);
            } else {
                events.forEach((evt) => window.removeEventListener(evt, yieldToUser, opts));
            }
        };
        requestAnimationFrame(retry);
    };

    // Every client-side visit that carries a hash. No-ops on hashless navigations.
    // 12 × 80ms ≈ 1s, which covers the plugin's reset/restore with room to spare
    // without holding the scroll hostage if something later moves it legitimately.
    swup?.hooks?.on('page:view', () => assertHashScroll(12));

    // The initial server-rendered load, where Swup's page:view never fires (the kit
    // mints its own PAGE_VIEW_EVENT instead). Longer window: this one is racing async
    // islands and font/image reflow rather than a single plugin.
    assertHashScroll(30);
}
